// ** React Imports
import { Fragment, useState, MouseEvent } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Table from '@mui/material/Table'
import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'

import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'

import TimelineItem from '@mui/lab/TimelineItem'
import CardHeader from '@mui/material/CardHeader'
import Typography from '@mui/material/Typography'
import TableContainer from '@mui/material/TableContainer'
import { v4 as uuid } from 'uuid';
import CustomChip from 'src/materio/@core/components/mui/chip'
// ** Types
import { loadPlaybooks, PlaybookCadence, PlaybookLibrary, PlaybookStore, saveCadence, saveLibrary } from '../PlaybookStore'
import { Button, IconButton, InputAdornment, Menu, MenuItem, TextField, Tooltip } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import EditCadence from './EditCadence'
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CadenceStepsList from './CadenceStepsList'
import RenderIf, { ClaimType, ClaimValue } from '../../common/RenderIf'
import DeleteIcon from '@mui/icons-material/Delete';
import useAwaitableComponent from '../../../hooks/useAwaitableComponent'
import ConfirmationDialog from '../../common/ConfirmationDialog'
import { ApiResponseType } from '../../../types'
import ApplicationStore from '../../../stores/ApplicationStore'
import { client } from '../../../utils/axios-client'
import TableRow from '../../../components/table/TableRow'
import TableCell from '../../../components/table/TableCell'
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';

const CadenceList = () => {
    const playbook = PlaybookStore.useState(s => s.selectedPlaybook);

    const [addOpen, setAddOpen] = useState(false);
    const [editLibraryOpen, setEditLibraryOpen] = useState(false);
    const [viewMode, setViewMode] = useState(false);
    const selectedCadence = PlaybookStore.useState(s => s.selectedCadence);
    const [stepsOpen, setStepsOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [status, execute, resolve, reject, reset] = useAwaitableComponent();
    const showModal = status !== "idle";
    const [filter, setFilter] = useState('');

    const handleConfirmDelete = async (row: PlaybookCadence) => {
        try {
            const value = await execute();
            const response = await client.delete<ApiResponseType>('/api/playbooks/cadence', { params: { cadenceId: row.id } });

            if (response.data.succeeded) {
                await loadPlaybooks('');

                ApplicationStore.update(s => {
                    s.snackbarMessage = "Cadence successfully deleted";
                    s.snackbarOpen = true;
                });
            } else {
                alert('Error while deleting cadence.');
            }
        } catch (err) {

        } finally {
            reset();
        }
    };

    const onAddLibrary = () => {
        PlaybookStore.update(s => {
            s.selectedCadence = { name: '', id: uuid() } as PlaybookCadence;
        })

        setAddOpen(true);
    }

    const onSaveCadence = async (row: PlaybookCadence) => {
        if (playbook === null) throw 'Playbook not selected';
        const response = await saveCadence({ ...row, id: uuid() }, playbook, '');

        if (response.succeeded) {
            setAddOpen(false);
        }

        return response;
    }

    const onUpdateCadence = async (row: PlaybookCadence) => {
        if (playbook === null) throw 'Playbook not selected';
        const response = await saveCadence({ ...row }, playbook, '');

        if (response.succeeded) {
            setEditOpen(false);
        }

        return response;
    }

    const onEdit = async (row: PlaybookCadence) => {
        PlaybookStore.update(s => {
            s.selectedCadence = row;
        })

        setEditOpen(true);
    }

    const onOpenSteps = async (row: PlaybookCadence) => {
        PlaybookStore.update(s => {
            s.selectedCadence = row;
        })

        setStepsOpen(true);
    }


    let cadences = structuredClone(playbook?.cadences ?? []);
    let filteredCadences = filter != '' ? cadences.filter(s => s.name.toLowerCase().includes(filter.toLowerCase())) : cadences;

    var collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    filteredCadences.sort((a, b) => collator.compare(a.name, b.name))

    return (
        <Fragment>
            <Card sx={{ mb: 6 }}>
                <CardHeader title='Playbook Cadences' titleTypographyProps={{ variant: 'h6' }} action={
                    <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.AddPlaybookCadence}>
                        <TextField size="small" sx={{ mr: 2 }} value={filter} onChange={(e) => setFilter(e.target.value)} InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}

                            inputProps={{
                                style: {
                                    padding: 4.5
                                }
                            }} />
                        <Button onClick={onAddLibrary} variant='contained' sx={{ mr: 1 }} style={{ color: 'white' }} size="small" endIcon={<AddIcon />}>
                            Add Cadence
                        </Button>
                    </RenderIf>
                } />


                <Divider sx={{ m: 0 }} />

                <TableContainer>
                    <Table size='small'>
                        <TableHead
                            sx={{ backgroundColor: theme => (theme.palette.mode === 'light' ? 'grey.50' : 'background.default') }}
                        >
                            <TableRow>
                                <TableCell basis={40} >Name</TableCell>
                                <TableCell basis={35} minBreakpoint='md'>Cadence Type</TableCell>
                                <TableCell basis={15} minBreakpoint='md' sx={{ textAlign: 'center' }}>Activities</TableCell>
                                <TableCell basis={15} minBreakpoint='md' sx={{ textAlign: 'center' }}>MIs</TableCell>
                                <TableCell basis={15} minBreakpoint='md' sx={{ textAlign: 'center' }}>MQLs</TableCell>
                                <TableCell basis={15} minBreakpoint='md' sx={{ textAlign: 'center' }}>DCs</TableCell>
                                <TableCell basis={15} minBreakpoint='md' sx={{ textAlign: 'center' }}>Opportunities</TableCell>
                                <TableCell basis={20} sx={{ textAlign: 'center' }}></TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {filteredCadences.map((item, index: number) => (
                                <TableRow hover key={index} sx={{ '&:last-of-type td': { border: 0 } }}>
                                    <TableCell basis={40}>
                                        <Box title={!item.nextCadenceId && item.endActionName.toUpperCase() == "NEW CADENCE" ? 'Next Candence Not Configured' : undefined}>
                                            <Typography sx={{ fontWeight: 500, fontSize: '0.875rem', display: 'flex', alignItems: 'end' }}>{!item.nextCadenceId && item.endActionName.toUpperCase() == "NEW CADENCE" && <WarningIcon style={{ marginRight: '5px' }} color="warning" />}{item.name}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell basis={35} minBreakpoint='md'>
                                        <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>{item.cadenceTypeName}</Typography>
                                    </TableCell>
                                    <TableCell basis={15} minBreakpoint='md' sx={{ textAlign: 'center' }}>
                                        <Typography>{item.executedCount}</Typography>
                                    </TableCell>
                                    <TableCell basis={15} minBreakpoint='md' sx={{ textAlign: 'center' }}>
                                        <Typography>{item.miCount}</Typography>
                                    </TableCell>
                                    <TableCell basis={15} minBreakpoint='md' sx={{ textAlign: 'center' }}>
                                        <Typography>{item.mqlCount}</Typography>
                                    </TableCell>
                                    <TableCell basis={15} minBreakpoint='md' sx={{ textAlign: 'center' }}>
                                        <Typography>{item.dcCount}</Typography>
                                    </TableCell>
                                    <TableCell basis={15} minBreakpoint='md' sx={{ textAlign: 'center' }}>
                                        <Typography>{item.opportunityCount}</Typography>
                                    </TableCell>
                                    <TableCell basis={20} sx={{ textAlign: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.DeletePlaybookCadence}>
                                                <IconButton onClick={() => handleConfirmDelete(item)} title="Delete Cadence">
                                                    <DeleteIcon fontSize='small' />
                                                </IconButton>
                                            </RenderIf>
                                            <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.EditPlaybookCadence}>
                                                <Tooltip title='Edit'>
                                                    <IconButton size='small' component='a' sx={{ textDecoration: 'none' }} onClick={() => onEdit(item)}>
                                                        <EditIcon fontSize='small' />
                                                    </IconButton>
                                                </Tooltip>
                                            </RenderIf>
                                            <RenderIf claimType={ClaimType.Playbooks} claimValue={ClaimValue.ViewPlaybookCadenceSteps}>
                                                <Tooltip title='Steps'>
                                                    <IconButton size='small' component='a' sx={{ textDecoration: 'none' }} onClick={() => onOpenSteps(item)}>
                                                        <AccountTreeIcon fontSize='small' />
                                                    </IconButton>
                                                </Tooltip>
                                            </RenderIf>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>

            {selectedCadence && <EditCadence open={addOpen} onClose={() => setAddOpen(false)} onSubmit={onSaveCadence} title="Add Cadence" subTitle="Please enter a name for the cadence" fieldLabel="Cadence Name" confirmButtonLabel="Create Cadence" record={selectedCadence} />}
            {selectedCadence && <EditCadence open={editOpen} onClose={() => setEditOpen(false)} onSubmit={onUpdateCadence} title="Edit Cadence" subTitle="Please enter a name for the cadence" fieldLabel="Cadence Name" confirmButtonLabel="Save Cadence" record={selectedCadence} />}
            {stepsOpen && <CadenceStepsList key={selectedCadence ? selectedCadence.id : ''} open={stepsOpen} onClose={() => { setStepsOpen(false); PlaybookStore.update(s => { s.selectedCadence = null; }) }} />}
            <ConfirmationDialog open={showModal} onConfirm={resolve} onCancel={reject} title="Delete?" message="Are you sure you want to delete the cadence?" confirmButtonText="Delete" cancelButtonText="Cancel" />
        </Fragment>
    )
}

export default CadenceList
